///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import {
	ContinuousGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type TouchPoint
} from "./GestureRecognizer"

export interface PinchGestureEventData extends GestureEventData {
	/** Scale factor relative to start (1.0 = no change) */
	scale: number
	/** Change in scale since last event */
	scaleChange: number
	/** Velocity of scale change (scale units per second) */
	scaleVelocity: number
	/** Rotation in radians relative to start (positive = clockwise) */
	rotation: number
	/** Change in rotation since last event (radians) */
	rotationChange: number
	/** Rotation in degrees for convenience */
	rotationDegrees: number
	/** Velocity of rotation (radians per second) */
	rotationVelocity: number
	/** Distance between the two touches */
	span: number
	/** Center point between the two fingers */
	centerX: number
	centerY: number
	/** The two touch points */
	touch0: { x: number; y: number }
	touch1: { x: number; y: number }
}

export interface PinchGestureConfig extends GestureRecognizerConfig {
	/** Minimum movement (scale or rotation) before pinch is recognized */
	minimumMovement?: number
}

/**
 * Generic pinch gesture recognizer - handles any two-finger gesture
 *
 * This recognizer tracks both scale and rotation simultaneously. It's designed
 * to be a fallback when the more specific ZoomGestureRecognizer and
 * RotateGestureRecognizer don't match the gesture pattern.
 *
 * Usage pattern:
 * 1. Add ZoomGestureRecognizer (higher priority) - fails if rotation dominates
 * 2. Add RotateGestureRecognizer (higher priority) - fails if scale dominates
 * 3. Add PinchGestureRecognizer (lower priority) - catches everything else
 *
 * The arena's conflict resolution will ensure only one recognizer wins.
 *
 * This recognizer is also useful when you want to handle both zoom and
 * rotation from a single gesture (e.g., map manipulation).
 */
export class PinchGestureRecognizer extends ContinuousGestureRecognizer<PinchGestureEventData> {
	private minimumMovement: number

	// Scale tracking
	private initialSpan: number = 0
	private currentSpan: number = 0
	public lastSpan: number = 0
	private scaleVelocity: number = 0

	// Rotation tracking
	public initialAngle: number = 0
	private currentAngle: number = 0
	private lastAngle: number = 0
	private totalRotation: number = 0
	private rotationVelocity: number = 0

	private lastMoveTime: number = 0

	// Track the two touch IDs for this pinch
	private touch0Id?: number
	private touch1Id?: number

	constructor(config: PinchGestureConfig = {}) {
		super({
			...config,
			minimumTouches: 2,
			maximumTouches: 2
		})
		this.minimumMovement = config.minimumMovement ?? 0.01
		// Pinch gestures should typically allow simultaneous recognition
		this._allowSimultaneous = config.allowSimultaneous ?? true
	}

	get gestureType(): string {
		return "pinch"
	}

	get scale(): number {
		if (this.initialSpan === 0) return 1
		return this.currentSpan / this.initialSpan
	}

	get rotation(): number {
		return this.totalRotation
	}

	handleTouchStart(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Update tracked touches
		touches.forEach(t => this.touches.set(t.id, t))

		// Need exactly 2 touches for pinch
		if (this.touchCount !== 2) {
			if (this.isActive()) {
				// Lost a touch - end the gesture
				const data = this.createEventData(GestureState.Ended)
				this.end(data)
			}
			return
		}

		// Get the two touch IDs
		const touchIds = Array.from(this.touches.keys())
		this.touch0Id = touchIds[0]
		this.touch1Id = touchIds[1]

		// Calculate initial span and angle
		const { span, angle } = this.calculateSpanAndAngle()
		this.initialSpan = span
		this.currentSpan = span
		this.lastSpan = span
		this.initialAngle = angle
		this.currentAngle = angle
		this.lastAngle = angle
		this.totalRotation = 0
		this.lastMoveTime = performance.now()
		this.scaleVelocity = 0
		this.rotationVelocity = 0
	}

	handleTouchMove(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Update tracked touches
		touches.forEach(t => {
			const existing = this.touches.get(t.id)
			if (existing) {
				existing.x = t.x
				existing.y = t.y
			}
		})

		// Need exactly 2 touches
		if (this.touchCount !== 2) return

		const now = performance.now()
		const dt = (now - this.lastMoveTime) / 1000

		// Calculate current span and angle
		const { span, angle } = this.calculateSpanAndAngle()
		const previousSpan = this.currentSpan
		this.currentSpan = span

		// Calculate rotation change (handling wrap-around)
		const rotationDelta = this.normalizeAngle(angle - this.lastAngle)
		this.totalRotation += rotationDelta
		this.currentAngle = angle

		// Calculate velocities
		if (dt > 0) {
			if (this.initialSpan > 0) {
				const scaleChange = (this.currentSpan - previousSpan) / this.initialSpan
				this.scaleVelocity = scaleChange / dt
			}
			this.rotationVelocity = rotationDelta / dt
		}

		this.lastSpan = this.currentSpan
		this.lastAngle = angle
		this.lastMoveTime = now

		if (!this.isActive()) {
			// Check if either scale change or rotation exceeds threshold
			const scaleChange = Math.abs(this.scale - 1)
			const absRotation = Math.abs(this.totalRotation)

			if (scaleChange >= this.minimumMovement || absRotation >= this.minimumMovement) {
				const data = this.createEventData(GestureState.Began)
				this.begin(data)
			}
		} else {
			// Emit change event
			const data = this.createEventData(GestureState.Changed)
			data.rotationChange = rotationDelta
			data.scaleChange = this.scale - (previousSpan / this.initialSpan)
			this.change(data)
		}
	}

	handleTouchEnd(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Remove ended touches
		touches.forEach(t => this.touches.delete(t.id))

		// End if we no longer have 2 touches
		if (this.touchCount < 2 && this.isActive()) {
			const data = this.createEventData(GestureState.Ended)
			this.end(data)
		} else if (!this.isActive() && !this.isTerminal()) {
			this.fail()
		}
	}

	handleTouchCancel(): void {
		this.cancel()
	}

	private calculateSpanAndAngle(): { span: number; angle: number } {
		if (this.touch0Id === undefined || this.touch1Id === undefined) {
			return { span: 0, angle: 0 }
		}

		const touch0 = this.touches.get(this.touch0Id)
		const touch1 = this.touches.get(this.touch1Id)

		if (!touch0 || !touch1) {
			return { span: 0, angle: 0 }
		}

		const dx = touch1.x - touch0.x
		const dy = touch1.y - touch0.y

		return {
			span: Math.sqrt(dx * dx + dy * dy),
			angle: Math.atan2(dy, dx)
		}
	}

	private normalizeAngle(angle: number): number {
		while (angle > Math.PI) angle -= 2 * Math.PI
		while (angle < -Math.PI) angle += 2 * Math.PI
		return angle
	}

	private getTouches(): { touch0: { x: number; y: number }; touch1: { x: number; y: number } } {
		const touch0 = this.touch0Id !== undefined ? this.touches.get(this.touch0Id) : undefined
		const touch1 = this.touch1Id !== undefined ? this.touches.get(this.touch1Id) : undefined

		return {
			touch0: touch0 ? { x: touch0.x, y: touch0.y } : { x: 0, y: 0 },
			touch1: touch1 ? { x: touch1.x, y: touch1.y } : { x: 0, y: 0 }
		}
	}

	protected createEventData(state: GestureState): PinchGestureEventData {
		const centroid = this.getCentroid()
		const touchPositions = this.getTouches()

		return {
			type: "pinch",
			state,
			timestamp: performance.now(),
			x: centroid.x,
			y: centroid.y,
			ownerHits: this.getOwnerHits(),
			scale: this.scale,
			scaleChange: 0, // Set by caller for Changed events
			scaleVelocity: this.scaleVelocity,
			rotation: this.totalRotation,
			rotationChange: 0, // Set by caller for Changed events
			rotationDegrees: this.totalRotation * (180 / Math.PI),
			rotationVelocity: this.rotationVelocity,
			span: this.currentSpan,
			centerX: centroid.x,
			centerY: centroid.y,
			touch0: touchPositions.touch0,
			touch1: touchPositions.touch1
		}
	}

	/** Reset scale to 1.0 at current position (for relative scaling) */
	public resetScale(): void {
		this.initialSpan = this.currentSpan
	}

	/** Reset rotation to 0 at current angle (for relative rotation) */
	public resetRotation(): void {
		this.totalRotation = 0
		this.initialAngle = this.currentAngle
	}

	override reset(): void {
		super.reset()
		this.initialSpan = 0
		this.currentSpan = 0
		this.lastSpan = 0
		this.scaleVelocity = 0
		this.initialAngle = 0
		this.currentAngle = 0
		this.lastAngle = 0
		this.totalRotation = 0
		this.rotationVelocity = 0
		this.lastMoveTime = 0
		this.touch0Id = undefined
		this.touch1Id = undefined
	}
}
